/**
 * System-managed CRM entity types: users, teams, imports, risks, research,
 * email threads, files, Stripe subscriptions. Split out of
 * entityTypeRegistry.ts — see that file for the merge point.
 */

import { getPropertyValue } from '@/types/crm';

import type { CrmObject } from '../../core/types';
import { getDisplayName } from '../../utils/getDisplayName';
import { FileRecordOverview } from '../files/FileRecordOverview';
import { EmailThreadConversation } from '../sections/email-thread-conversation';
import { EmailThreadSubHeader } from '../sections/email-thread-sub-header';
import { ResearchBriefSection } from '../sections/research-brief-section';
import { EVENT_TYPE_CATEGORIES } from '../timeline/types';
import type { EntityTypeConfig } from './entityTypeRegistry';

/** System-managed CRM entity types: users, teams, imports, risks, research, email threads, files, subscriptions. */
export const SYSTEM_ENTITY_TYPES: Record<string, EntityTypeConfig> = {
  ahoy_user: {
    displayName: 'User',
    pluralDisplayName: 'Users',
    color: 'gray',
    bannerGradient: 'linear-gradient(135deg, #64748B 0%, #3B82F6 100%)', // Gray to Blue
    getDisplayName,
    detailFieldOrder: [
      { type: 'property', key: 'ahoy_email' },
      { type: 'association', key: 'ahoy_user_to_role' },
      { type: 'association', key: 'ahoy_user_to_team' },
      { type: 'timestamp', key: 'ahoy_created_at' },
      { type: 'timestamp', key: 'ahoy_updated_at' },
    ],
    routes: {
      list: '/:orgId/crm/users',
      detail: '/:orgId/crm/ahoy_user/:id',
    },
  },

  ahoy_team: {
    displayName: 'Team',
    pluralDisplayName: 'Teams',
    color: 'purple',
    bannerGradient: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
    getDisplayName: obj =>
      getPropertyValue<string>(obj.properties?.ahoy_display_name) ?? 'Team',
    detailFieldOrder: [
      { type: 'property', key: 'ahoy_description' },
      { type: 'association', key: 'ahoy_team_to_user' },
      { type: 'timestamp', key: 'ahoy_created_at' },
      { type: 'timestamp', key: 'ahoy_updated_at' },
    ],
    routes: {
      list: '/:orgId/settings/teams',
      detail: '/:orgId/crm/ahoy_team/:id',
    },
  },

  ahoy_import: {
    displayName: 'Import',
    pluralDisplayName: 'Imports',
    color: 'gray',
    bannerGradient: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)', // Gray to Slate
    getDisplayName,
    routes: {
      list: '/:orgId/crm/imports', // Dedicated imports list page
      detail: '/:orgId/crm/ahoy_import/:id',
    },
  },

  ahoy_risk: {
    displayName: 'Risk',
    pluralDisplayName: 'Risks',
    color: 'red',
    bannerGradient: 'linear-gradient(135deg, #EF4444 0%, #F97316 100%)', // Red to Orange
    getDisplayName: (object: CrmObject) => {
      // First check ahoy_display_name (standard behavior)
      const displayNameProp = object.properties?.ahoy_display_name;
      const displayName = getPropertyValue<string>(displayNameProp);
      const normalizedDisplayName =
        typeof displayName === 'string' ? displayName.trim() : '';
      if (normalizedDisplayName) {
        return normalizedDisplayName;
      }
      // Fall back to ahoy_title for risk-specific behavior
      const titleProp = object.properties?.ahoy_title;
      const title = getPropertyValue<string>(titleProp);
      const normalizedTitle = typeof title === 'string' ? title.trim() : '';
      if (normalizedTitle) {
        return normalizedTitle;
      }
      return 'Risk Signal';
    },
    timelineConfig: {
      includeAssociatedEvents: false,
      associatedEventTypes: [],
      groupByDate: true,
      defaultFilters: {},
    },
    routes: {
      list: '/:orgId/crm/ahoy_risk',
      detail: '/:orgId/crm/ahoy_risk/:id',
    },
  },

  ahoy_research: {
    displayName: 'Research',
    pluralDisplayName: 'Research',
    color: 'cyan',
    bannerGradient: 'linear-gradient(135deg, #67e8f9 0%, #06b6d4 100%)', // Cyan gradient
    getDisplayName: (object: CrmObject) => {
      // First check ahoy_display_name (standard behavior)
      const displayNameProp = object.properties?.ahoy_display_name;
      const displayName = getPropertyValue<string>(displayNameProp);
      const normalizedDisplayName =
        typeof displayName === 'string' ? displayName.trim() : '';
      if (normalizedDisplayName) {
        return normalizedDisplayName;
      }
      // Fall back to ahoy_title for research-specific behavior
      const titleProp = object.properties?.ahoy_title;
      const title = getPropertyValue<string>(titleProp);
      if (title) {
        return String(title);
      }
      // Final fallback: use research type
      const researchTypeProp = object.properties?.ahoy_research_type;
      const researchType = getPropertyValue<string>(researchTypeProp);
      return researchType ? `${researchType} Research` : 'Research';
    },
    hiddenProperties: ['ahoy_summary'],
    routes: {
      list: '/:orgId/crm/ahoy_research',
      detail: '/:orgId/crm/ahoy_research/:id',
    },
    components: {
      typeSpecific: ResearchBriefSection,
    },
  },

  ahoy_email_thread: {
    displayName: 'Email Thread',
    pluralDisplayName: 'Email Threads',
    color: 'teal',
    bannerGradient: 'linear-gradient(135deg, #14B8A6 0%, #3B82F6 100%)',
    getDisplayName: (obj: CrmObject) => {
      const displayName = getPropertyValue<string>(
        obj.properties?.ahoy_display_name
      );
      return typeof displayName === 'string' && displayName.trim()
        ? displayName.trim()
        : 'Email Thread';
    },
    readOnly: true,
    showAvatar: false,
    headerVariant: 'compact',
    routes: {
      list: '/:orgId/crm/email-threads',
      detail: '/:orgId/crm/ahoy_email_thread/:id',
    },
    highlights: [
      {
        kind: 'entity_count',
        label: 'Messages',
        filter: { entityType: 'event', associationType: 'ahoy_email' },
      },
      { kind: 'last_event', label: 'Last Reply', eventGroup: 'ahoy_email' },
      {
        kind: 'association_count',
        label: 'Participants',
        associationType: 'ahoy_contact_to_email_thread',
      },
    ],
    tabs: ['overview', 'activity', 'emails', 'notes', 'associations'],
    // Open the timeline showing emails only — an email thread's activity IS
    // its messages, so lifecycle/association noise would flood the view.
    // includeAssociated MUST be explicitly false: consumers fall back
    // inconsistently when it is omitted (timeline-section defaults false,
    // CRMObjectFullPageView and TimelineTab default true), and a true
    // fallback re-enables the association fan-out this config exists to
    // prevent (the email-thread timeline flood).
    timelineConfig: {
      includeAssociatedEvents: false,
      associatedEventTypes: [],
      groupByDate: true,
      defaultFilters: {
        eventTypes: [...EVENT_TYPE_CATEGORIES.emails],
        includeAssociated: false,
      },
    },
    // The slideout body is the conversation view (`overview` override): the
    // thread's email events rendered oldest-first as readable messages. The
    // generic TimelineSection is suppressed for this type — it would repeat
    // the same events as a reverse-chronological log directly beneath the
    // conversation. The full-page Activity tab still carries the generic
    // timeline for audit-style browsing.
    hideTimelineSection: true,
    // No summary section: participants live in the sub-header as a compact
    // avatar stack (EmailThreadParticipantsStack inside EmailThreadSubHeader,
    // full pill list behind its popover) so the conversation starts higher.
    // TODO: when an AI-generated thread summary exists, it takes this slot.
    components: {
      summary: null,
      subHeader: EmailThreadSubHeader,
      overview: EmailThreadConversation,
    },
  },

  ahoy_file: {
    displayName: 'File',
    pluralDisplayName: 'Files',
    color: 'gray',
    bannerGradient: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)',
    getDisplayName,
    headerVariant: 'compact',
    readOnly: true,
    showAvatar: false,
    components: {
      typeSpecific: FileRecordOverview,
    },
  },

  ahoy_stripe_subscription: {
    displayName: 'Subscription',
    pluralDisplayName: 'Subscriptions',
    color: 'purple',
    bannerGradient: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)',
    getDisplayName,
    routes: {
      list: '/:orgId/crm/ahoy_stripe_subscription',
      detail: '/:orgId/crm/ahoy_stripe_subscription/:id',
    },
    hiddenProperties: [
      'ahoy_stripe_subscription_id',
      'ahoy_stripe_customer_id',
      'ahoy_hidden',
    ],
    tabs: ['overview', 'activity', 'associations'],
  },
};
